import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Switch from '@material-ui/core/Switch';
import { MuiPickersUtilsProvider } from 'material-ui-pickers';
import MomentUtils from '@date-io/moment';
import CircularProgress from 'material-ui/CircularProgress/CircularProgress';
import Tooltip from '@ublend-npm/react-tooltip';
import { MuiThemeProvider, MenuItem } from '@material-ui/core';

import AnonymousIcon from '../../../AnonymousIcon';
import {
  ModalContent,
  Title,
  SubTitle,
  StyledPickerLeft,
  StyledPickerRight,
  SwitchContainer,
  SwitchLabel,
  StyledTextField,
  datePickerClass,
  SwitchTitleContainer,
  HiddenIcon,
  providerCapitalize,
  formTheme,
  GradeTypeSelect,
  MaxGrade,
} from './CreateModal.styled';
import gradeTypes from './gradeTypes';

class CreateModal extends PureComponent {
  renderSuccessScreen() {
    const { provider } = this.props;

    return (
      <React.Fragment>
        <Title>Excellent!</Title>
        <SubTitle>
          Your new assignment has been created in{' '}
          <span className={providerCapitalize}>{provider}</span>!
        </SubTitle>
      </React.Fragment>
    );
  }

  renderAnonymiseSwitch() {
    const {
      assignment,
      handleSwitch,
      isEditing,
      hasAnonymizedError,
    } = this.props;
    const { isAnonymised } = assignment;
    const hasAnonymizedOption = typeof isAnonymised === 'boolean';
    const shouldShowAnonymizedOption =
      !isEditing || (isEditing && hasAnonymizedOption);
    const hasSubmissions = assignment && assignment.submissions.length > 0;
    const isGradesReleasedPassed =
      new Date(assignment.gradesReleasedAt) < new Date();

    const disabledSwitch =
      hasSubmissions || hasAnonymizedError || isGradesReleasedPassed;

    const tooptipText = disabledSwitch
      ? `The anonymise setting cannot be modified because it has already received submissions or grades released date is in the past.`
      : 'By default, student submissions will not be anonymised. Change this setting here.';

    return shouldShowAnonymizedOption ? (
      <Tooltip position="top" title={tooptipText}>
        <SwitchContainer isTop>
          <SwitchTitleContainer>
            <AnonymousIcon />
            <SwitchLabel>Anonymise student submissions</SwitchLabel>
          </SwitchTitleContainer>
          <Switch
            disabled={disabledSwitch}
            checked={isAnonymised}
            onChange={handleSwitch('isAnonymised')}
            color="primary"
          />
        </SwitchContainer>
      </Tooltip>
    ) : null;
  }

  renderForm() {
    const {
      errors,
      assignment,
      handleChange,
      handleDateTimePicker,
      handleSwitch,
      isEditing,
      tiiMaxGrade,
      handleGradeTypeChange,
      handleMaxGradeChange,
    } = this.props;

    const {
      title,
      startDate,
      endDate,
      gradesReleasedAt,
      isHidden,
      description,
      gradeType,
      maxGrade,
    } = assignment;

    return (
      <ModalContent data-testid="new-assignment-info">
        <MuiThemeProvider theme={formTheme}>
          <Title>Assignment info</Title>
          <StyledTextField
            fullWidth
            label="Title"
            data-testid="new-assignment-title"
            value={title}
            onChange={handleChange('title')}
            error={errors.title}
          />
          <StyledTextField
            fullWidth
            label="Description"
            data-testid="new-assignment-description"
            value={description}
            onChange={handleChange('description')}
            error={errors.description}
          />
          <div>
            <StyledPickerLeft
              autoOk
              ampm
              value={startDate}
              ViewContainerComponent={datePickerClass}
              onChange={handleDateTimePicker('startDate')}
              label="Open date"
              error={errors.startDate}
              data-testid="new-assignment-start-button"
              DialogProps={{
                'data-testid': 'new-assignment-start-date-picker',
              }}
            />
            <StyledPickerRight
              autoOk
              ampm
              value={endDate}
              onChange={handleDateTimePicker('endDate')}
              label="Due date"
              error={errors.endDate}
              data-testid="new-assignment-due-button"
              DialogProps={{
                'data-testid': 'new-assignment-due-date-picker',
              }}
            />
          </div>
          <div>
            <StyledPickerLeft
              autoOk
              ampm
              value={gradesReleasedAt || null}
              ViewContainerComponent={datePickerClass}
              onChange={handleDateTimePicker('gradesReleasedAt')}
              label="Grades release date"
              error={errors.gradesReleasedAt}
              data-testid="new-assignment-released-button"
              DialogProps={{
                'data-testid': 'new-assignment-released-date-picker',
              }}
            />
          </div>
          {tiiMaxGrade && (
            <div>
              <GradeTypeSelect
                select
                label="Grading type"
                value={gradeType}
                onChange={handleGradeTypeChange}
                margin="normal"
                disabled={isEditing}
                data-testid="new-assignment-grading-type"
              >
                {Object.values(gradeTypes).map((type) => (
                  <MenuItem
                    key={type.value}
                    value={type.value}
                    data-testid={`grading-type-${type.value}`}
                  >
                    {type.displayValue}
                  </MenuItem>
                ))}
              </GradeTypeSelect>
              {gradeType === gradeTypes.points.value && (
                <MaxGrade
                  value={maxGrade}
                  type="number"
                  label="Max grade"
                  onChange={handleMaxGradeChange}
                  error={errors.maxGrade}
                  InputProps={{ inputProps: { min: 1, max: 1000 } }}
                  disabled={isEditing}
                  data-testid="new-assignment-max-grade"
                />
              )}
            </div>
          )}
          <Tooltip
            position="top"
            title="By default, assignments are hidden from students until you are ready to make them visible."
          >
            <SwitchContainer checked={isHidden} isTop>
              <SwitchTitleContainer>
                <HiddenIcon />
                <SwitchLabel>Assignment is hidden</SwitchLabel>
              </SwitchTitleContainer>
              <Switch
                checked={isHidden}
                onChange={handleSwitch('isHidden')}
                color="primary"
              />
            </SwitchContainer>
          </Tooltip>
          {this.renderAnonymiseSwitch()}
        </MuiThemeProvider>
      </ModalContent>
    );
  }

  render() {
    const { isSaving, done } = this.props;

    return (
      <MuiPickersUtilsProvider utils={MomentUtils}>
        {!isSaving && !done && this.renderForm()}
        {isSaving && <CircularProgress />}
        {done && this.renderSuccessScreen()}
      </MuiPickersUtilsProvider>
    );
  }
}

CreateModal.defaultProps = {
  tiiMaxGrade: undefined,
};

CreateModal.propTypes = {
  errors: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isSaving: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  tiiMaxGrade: PropTypes.number,
  done: PropTypes.bool.isRequired,
  hasAnonymizedError: PropTypes.bool.isRequired,
  provider: PropTypes.string.isRequired,
  assignment: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  handleChange: PropTypes.func.isRequired,
  handleDateTimePicker: PropTypes.func.isRequired,
  handleSwitch: PropTypes.func.isRequired,
  handleGradeTypeChange: PropTypes.func.isRequired,
  handleMaxGradeChange: PropTypes.func.isRequired,
};

export default CreateModal;
